import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';

/**
 * Honest Google Ads read wait. A bare "Loading…" reads as broken.
 * Always says the wait and that we depend on Google. Optional rotating
 * one-liner. No fake percent bar.
 */
export const ADS_WAIT_LINE =
  "This can take one to two minutes. We're waiting on Google Ads.";

export const ADS_WAIT_ASIDES = [
  "We know it stinks waiting — we're dependent on Google for this.",
  'Please bear with us for a couple of minutes.',
  'This is a live read from Google Ads, not a stuck spinner.',
] as const;

const ASIDE_MS = 8_000;

export function AdsWaitState({
  stage,
  className,
  compact = false,
}: {
  stage: string;
  className?: string;
  compact?: boolean;
}) {
  const [asideIndex, setAsideIndex] = useState(0);

  useEffect(() => {
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return undefined;
    const id = window.setInterval(() => {
      setAsideIndex((current) => (current + 1) % ADS_WAIT_ASIDES.length);
    }, ASIDE_MS);
    return () => window.clearInterval(id);
  }, []);

  return (
    <div
      className={cn(
        compact
          ? 'flex items-start gap-2 py-3 text-sm text-muted-foreground'
          : 'flex items-start gap-2 py-6 text-sm text-muted-foreground',
        className,
      )}
      role="status"
      aria-live="polite"
    >
      <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin" />
      <div className="space-y-1">
        <div className="font-medium text-foreground">{stage}</div>
        <div>{ADS_WAIT_LINE}</div>
        <div className="text-xs">{ADS_WAIT_ASIDES[asideIndex]}</div>
      </div>
    </div>
  );
}
